import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { forgotPassword } from '@/services/api/auth';
import { usePaperTheme } from '@/hooks/usePaperTheme';

export function ForgotPasswordPage() {
  usePaperTheme();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setLoading(true);
    try {
      await forgotPassword(trimmed);
      setSent(true);
    } catch (err) {
      toast.error((err as Error).message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-charcoal-soft">
      <nav className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
        <Link to="/" className="text-lg font-semibold tracking-tight-ish text-charcoal">
          clyra<span className="text-teal-500">.</span>
        </Link>
        <Link to="/login" className="text-petit text-charcoal-muted hover:text-charcoal transition-colors">
          Sign in
        </Link>
      </nav>

      <main className="mx-auto flex max-w-sm flex-col px-6 pt-12 pb-20">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold tracking-tight-ish text-charcoal">
            Forgot your password?
          </h1>
          <p className="mt-1 text-petit text-charcoal-muted">
            Enter your email and we'll send a reset link.
          </p>
        </div>

        <div className="rounded-xl border border-stone-subtle bg-paper-raised p-6 shadow-paper-sm">
          {sent ? (
            <div className="space-y-3 text-center">
              <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-teal-50 text-teal-600">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <p className="text-sm font-medium text-charcoal">Check your inbox.</p>
              <p className="text-petit text-charcoal-muted leading-relaxed">
                If <span className="font-medium text-charcoal-soft">{email}</span> is registered, a reset link is on its way. The link is good for one hour.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
              />
              <Button type="submit" className="w-full" loading={loading} disabled={!email.trim()}>
                Send reset link
              </Button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-petit text-charcoal-muted">
          Remembered it?{' '}
          <Link to="/login" className="text-teal-600 hover:text-teal-500 transition-colors">
            Sign in
          </Link>
        </p>
      </main>
    </div>
  );
}
